/* mad-swing-svg.js: top-down (plan view) door swing drawings for the wizard's swing tiles.
   Every drawing is seen from OUTSIDE the room looking in: inside is at the top, the wall runs
   across the middle, the eye at the bottom right is the viewer. What each code means, and why,
   is written down in docs/swing-diagram-convention.md. Lines use currentColor so a tile
   inherits the theme text colour; the arc uses the muted theme accent.
   window.MadSwingSVG = { single(code), pair(code), for(code), describe(code), viewpointNote() }
   Seth 2026-10-13: an interior pair has no stationary door, so its swing is Push or Pull (codes push and pull, both
   doors drawn active); the left or right hand active codes are for exterior pairs, which always have a stationary
   door. Every tile reads Push or Pull first. */
(function () {
  'use strict';
  if (window.MadSwingSVG) return;

  /* The shop's chart (and the door trade) reads the hand of an OUT-swing door from inside, the side
     you push from, so from outside a left-hand out-swing has its hinges on the right. Set this to
     false to read every hand from outside instead; the drawings and hint text follow. */
  var OUTSWING_HAND_FROM_INSIDE = true;

  var VB = '0 0 160 112', WY = 58, ACCENT = 'var(--mad-swing-accent, #716a56)';
  var NAMES = {
    lhis: 'Left hand, in-swing', rhis: 'Right hand, in-swing', lhos: 'Left hand, out-swing', rhos: 'Right hand, out-swing',
    lhais: 'Left hand active, in-swing', rhais: 'Right hand active, in-swing', lhaos: 'Left hand active, out-swing', rhaos: 'Right hand active, out-swing',
    push: 'Push swing', pull: 'Pull swing'
  };
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

  /* code -> facts. hingeLeft is the hinge jamb (or the active leaf) as seen from outside. */
  function geom(code) {
    var c = String(code || '').toLowerCase();
    /* push and pull: an interior pair, both doors open */
    if (c === 'push' || c === 'pull') return { code: c, pair: true, both: true, leftHand: true, inswing: c === 'push', hingeLeft: true, sy: c === 'push' ? -1 : 1 };
    var leftHand = c.charAt(0) === 'l', inswing = c.slice(-2) === 'is';
    var hingeLeft = (inswing || !OUTSWING_HAND_FROM_INSIDE) ? leftHand : !leftHand;
    return { code: c, pair: c.length === 5, leftHand: leftHand, inswing: inswing, hingeLeft: hingeLeft, sy: inswing ? -1 : 1 };
  }

  /* plain (Release B, Seth 2026-10-12: few words, the drawing first): the tile's sentence, said from outside the room
     like every drawing, from the same geometry, so the convention switch above still drives every word; push is
     "Push" (in-swing) or "Pull" (out-swing). The codes, the drawings and NAMES are unchanged. */
  function describe(code) {
    var g = geom(code), side = g.hingeLeft ? 'left' : 'right', dir = g.inswing ? 'away from you (push)' : 'toward you (pull)';
    var name = NAMES[g.code] || g.code.toUpperCase(), push = g.inswing ? 'Push' : 'Pull';
    if (g.both) {
      var both = 'Both doors open ' + (g.inswing ? 'away from you' : 'toward you');
      return { code: '', name: name, hint: both, title: name + '. ' + both + '.', plain: push, push: push };
    }
    var hint = g.pair ? 'From outside: the ' + side + ' leaf opens first, ' + dir
                      : 'From outside: hinges on your ' + side + ', opens ' + dir;
    /* Seth 2026-10-13: Push or Pull first, in the fewest words */
    var plain = g.pair ? push + ', the ' + side + ' door opens first' : push + ', hinges on your ' + side;
    return { code: g.code.toUpperCase(), name: name, hint: hint, title: name + '. ' + hint + '.', plain: plain, push: push };
  }

  /* a point on the swing circle: hx hinge x, sx +1 when the leaf closes toward the right, sy -1 in, +1 out */
  function pt(hx, sx, sy, deg, rho) {
    var a = deg * Math.PI / 180;
    return (hx + sx * rho * Math.cos(a)).toFixed(1) + ' ' + (WY + sy * rho * Math.sin(a)).toFixed(1);
  }

  /* one leaf: hinge dot, the leaf standing open at 90 degrees, its quarter-circle arc and an arrowhead in the opening direction */
  function leaf(hx, sx, sy, r, active) {
    var tipY = WY + sy * r, sweep = sx * sy > 0 ? 1 : 0;
    return '<g' + (active ? '' : ' opacity=".32"') + '>' +
      '<path d="M' + (hx + sx * r) + ' ' + WY + ' A' + r + ' ' + r + ' 0 0 ' + sweep + ' ' + hx + ' ' + tipY + '" fill="none" stroke-width="1.6" style="stroke:' + ACCENT + '"/>' +
      '<polygon points="' + pt(hx, sx, sy, 58, r) + ',' + pt(hx, sx, sy, 47, r - 4.5) + ',' + pt(hx, sx, sy, 47, r + 4.5) + '" style="fill:' + ACCENT + '"/>' +
      '<line x1="' + hx + '" y1="' + WY + '" x2="' + hx + '" y2="' + tipY + '" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>' +
      '<circle cx="' + hx + '" cy="' + WY + '" r="4.2" fill="currentColor"/></g>';
  }

  /* the wall with its opening, the INSIDE and OUTSIDE labels and the viewer's eye */
  function frame(ol, or) {
    return '<g stroke="currentColor" stroke-width="5"><line x1="4" y1="' + WY + '" x2="' + ol + '" y2="' + WY + '"/><line x1="' + or + '" y1="' + WY + '" x2="156" y2="' + WY + '"/></g>' +
      '<g fill="currentColor" font-size="8" letter-spacing=".6" opacity=".6"><text x="154" y="13" text-anchor="end">INSIDE</text><text x="6" y="108">OUTSIDE</text></g>' +
      eye(141, 95, 12) + '<text x="141" y="109" text-anchor="middle" font-size="7.5" fill="currentColor" opacity=".7">you</text>';
  }
  function eye(cx, cy, w) {
    return '<path d="M' + (cx - w) + ' ' + cy + ' Q' + cx + ' ' + (cy - w * 0.85) + ' ' + (cx + w) + ' ' + cy + ' Q' + cx + ' ' + (cy + w * 0.85) + ' ' + (cx - w) + ' ' + cy + 'Z" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="' + cx + '" cy="' + cy + '" r="' + (w / 4) + '" fill="currentColor"/>';
  }
  function open(code) {
    return '<svg class="mad-swing-svg" viewBox="' + VB + '" role="img" focusable="false" data-swing="' + esc(geom(code).code) + '"><title>' + esc(describe(code).title) + '</title>';
  }

  function single(code) {
    var g = geom(code), r = 46, hx = g.hingeLeft ? 57 : 103, sx = g.hingeLeft ? 1 : -1;
    return open(code) + frame(57, 103) + leaf(hx, sx, g.sy, r, true) + '</svg>';
  }

  function pair(code) {
    var g = geom(code), r = 36, left = g.hingeLeft, ax = left ? 44 : 116, asx = left ? 1 : -1;
    /* an interior pair: both doors drawn active, no "active" word */
    if (g.both) return open(code) + frame(44, 116) + leaf(116, -1, g.sy, r, true) + leaf(44, 1, g.sy, r, true) + '</svg>';
    var label = '<text x="' + (ax + asx * 4) + '" y="' + (g.sy < 0 ? 19 : 103) + '" text-anchor="' + (left ? 'start' : 'end') + '" font-size="7.5" font-weight="600" fill="currentColor">active</text>';
    return open(code) + frame(44, 116) + leaf(116, -1, g.sy, r, !left) + leaf(44, 1, g.sy, r, left) + label + '</svg>';
  }

  /* the one "you are here" note above the tiles */
  function viewpointNote() {
    return '<p class="mad-help mad-swing-note"><svg class="mad-swing-eye" viewBox="0 0 30 22" aria-hidden="true" focusable="false">' + eye(15, 11, 13) + '</svg>' +
      '<span>Seen from above, standing outside the room.</span></p>';
  }

  window.MadSwingSVG = { single: single, pair: pair, describe: describe, viewpointNote: viewpointNote, for: function (code) { return geom(code).pair ? pair(code) : single(code); } };
})();
